import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { auth, fileUrl } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { toast } from "sonner";
import { HeartPulse, ShieldCheck, Sparkles, Languages, Lock, AtSign, Loader2 } from "lucide-react";

export default function Login() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { branding, reloadUser } = useBranding();
  const brandColor = branding?.theme_color || "#0EA5E9";

  async function submit(e) {
    e.preventDefault();
    if (!identifier.trim() || !password) { toast.error("Please enter your credentials"); return; }
    setLoading(true);
    try {
      const data = await auth.universalLogin(identifier.trim(), password);
      auth.saveToken(data.token); auth.saveUser(data.user);
      await reloadUser?.();
      toast.success(`Welcome, ${data.user.name}`);
      nav(data.user.role === "employee" ? "/my" : "/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(msg);
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Left visual panel */}
      <div className="hidden lg:flex flex-col justify-between p-10 text-white relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${brandColor}, ${brandColor}dd 60%, ${brandColor}aa)` }}>
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="relative flex items-center gap-3">
          {branding?.logo_path ? (
            <img src={fileUrl(branding.logo_path)} alt="" className="h-12 w-12 rounded-2xl object-cover bg-white/15 backdrop-blur"/>
          ) : (
            <div className="h-12 w-12 rounded-2xl bg-white/15 backdrop-blur grid place-items-center">
              <HeartPulse className="h-6 w-6" />
            </div>
          )}
          <div>
            <div className="font-display font-black text-2xl leading-none">{branding?.hospital_name || "MedLearn"}</div>
            <div className="text-xs uppercase tracking-[0.22em] text-white/80">{branding?.tagline || "Hospital Training Platform"}</div>
          </div>
        </div>

        <div className="relative space-y-6 max-w-md">
          <h1 className="font-display font-black text-4xl lg:text-5xl leading-tight">
            Train smarter.<br/>Care better.
          </h1>
          <p className="text-white/90 text-lg leading-relaxed">
            Bilingual, gamified, role-aware training — for every team member from doctors to housekeeping.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Feature icon={Sparkles} title="AI Quiz Builder" desc="Generate bilingual quizzes from any topic."/>
            <Feature icon={Languages} title="Hindi + English" desc="Toggle anytime during the quiz."/>
            <Feature icon={ShieldCheck} title="Location-Locked" desc="Geo-fenced by site for compliance."/>
            <Feature icon={Lock} title="ISO 27001 Aligned" desc="Audit logs, RBAC, encrypted secrets."/>
          </div>
        </div>
        <div className="relative text-xs text-white/70">© {branding?.hospital_name || "MedLearn"} · All rights reserved</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6 bg-white p-8 rounded-3xl border border-slate-200 shadow-sm" data-testid="login-form">
          <div className="text-center lg:text-left">
            {/* Mobile-only header (logo) */}
            <div className="lg:hidden flex items-center justify-center gap-3 mb-2">
              {branding?.logo_path ? (
                <img src={fileUrl(branding.logo_path)} alt="" className="h-10 w-10 rounded-xl object-cover"/>
              ) : (
                <div className="h-10 w-10 rounded-xl grid place-items-center text-white" style={{ background: brandColor }}>
                  <HeartPulse className="h-5 w-5"/>
                </div>
              )}
              <div className="font-display font-black text-lg">{branding?.hospital_name || "MedLearn"}</div>
            </div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: brandColor }}>Sign in</div>
            <h2 className="font-display font-black text-3xl mt-2 text-slate-900">Welcome 👋</h2>
            <p className="text-slate-500 mt-1 text-sm">Sign in with your <strong>Email</strong> (admin/trainer) or <strong>Employee ID</strong>.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="identifier">Email or Employee ID</Label>
            <div className="relative">
              <AtSign className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"/>
              <Input id="identifier" type="text" autoComplete="username"
                value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                data-testid="login-identifier"
                className="rounded-xl pl-9 h-11" placeholder="you@hospital.com or EMP001" required autoFocus />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <button type="button" onClick={() => setShowPwd(s => !s)} className="text-xs text-slate-400 hover:text-slate-700" data-testid="toggle-pwd">
                {showPwd ? "Hide" : "Show"}
              </button>
            </div>
            <div className="relative">
              <Lock className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"/>
              <Input id="password" type={showPwd ? "text" : "password"} autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" className="rounded-xl pl-9 h-11" required />
            </div>
          </div>

          <Button type="submit" disabled={loading}
            data-testid="login-submit"
            className="w-full h-11 rounded-xl text-white font-bold" style={{ background: brandColor }}>
            {loading ? <Loader2 className="h-5 w-5 animate-spin"/> : "Sign in securely"}
          </Button>

          <div className="pt-3 border-t border-slate-100 text-[11px] text-center text-slate-400 flex items-center justify-center gap-1.5">
            <Lock className="h-3 w-3"/>Encrypted · audit-logged · ISO 27001-aligned controls
          </div>
        </form>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-2xl bg-white/10 backdrop-blur p-3">
      <Icon className="h-4 w-4 mb-1.5" />
      <div className="font-bold text-sm">{title}</div>
      <div className="text-[11px] text-white/80 mt-0.5 leading-tight">{desc}</div>
    </div>
  );
}
